/**
 * DevToolbar — Phase 1 developer controls (toggle with the gear button or ?dev=1).
 *   * environment: cross-origin isolation, input mode, OffscreenCanvas, worker rAF
 *   * live slots (N-1 / N / N+1) with states, compile timings, GL flavour, missing imports
 *   * memory against the 150MB budget (measureUserAgentSpecificMemory covers the workers)
 *   * telemetry transport status + the last shipped batch
 *   * actions: upload a game (.zip/.wasm), inject a mock telemetry batch, crash the active
 *     game, prefetch N+2, clear the game cache, force the postMessage input path
 */
import { For, Show, createSignal, onCleanup, onMount } from 'solid-js';
import type { CarouselApi } from './Carousel';
import type { TelemetryTransport, TransportStatus } from '../telemetry/TelemetryTransport';
import type { MechanicalTelemetry } from '../telemetry/TelemetryTypes';
import { MEMORY_BUDGET_BYTES, formatMB, measureMemory, type MemorySample } from '../lib/memory';
import { cacheStatus, clearGameCache, serviceWorkerActive, type CacheStatus } from '../lib/ServiceWorker';
import { importGameFile } from '../lib/zipImport';
import { isSharedInputAvailable } from '../lib/InputPipeline';

export interface DevToolbarProps {
  api: () => CarouselApi | null;
  transport: TelemetryTransport;
  activeIndex: number;
  onClose: () => void;
  toast: (message: string) => void;
}

export function DevToolbar(props: DevToolbarProps) {
  const [tick, setTick] = createSignal(0);
  const [memory, setMemory] = createSignal<MemorySample | null>(null);
  const [cache, setCache] = createSignal<CacheStatus | null>(null);
  const [status, setStatus] = createSignal<TransportStatus>(props.transport.getStatus());
  let fileInput!: HTMLInputElement;

  const refresh = async () => {
    setTick((t) => t + 1);
    setStatus(props.transport.getStatus());
    setMemory(await measureMemory());
    setCache(await cacheStatus());
  };

  onMount(() => {
    void refresh();
    const t = window.setInterval(() => void refresh(), 2500);
    onCleanup(() => clearInterval(t));
  });

  const hosts = () => {
    tick();
    return props.api()?.hosts() ?? [];
  };
  const lastBatch = (): MechanicalTelemetry | null => {
    tick();
    const log = props.transport.log;
    return log.length ? log[log.length - 1] : null;
  };

  const injectMock = () => {
    const active = props.api()?.activeHost();
    props.transport.send({
      session_uuid: 'mock-' + Math.random().toString(36).slice(2, 10),
      user_id: 'dev-toolbar',
      card_class: 'solo',
      referrer_user_id: null, // stamped by the transport
      entry_surface: 'direct', // stamped by the transport
      game_id: active?.meta.id ?? 'mock-game',
      timestamp_ms: Date.now() - 12_000,
      actions_per_minute: 84,
      avg_decision_latency_ms: 412,
      fail_state_count: 2,
      micro_session_duration_ms: 12_000,
      exit_condition: 'SWIPE_UP',
      ui_interaction_density: 0.31,
      action_count: 17,
      input_event_count: 240,
      decision_samples: 9,
      p50_decision_latency_ms: 380,
      avg_fps: 59.4,
      avg_input_latency_ms: 11.2,
      avg_pipeline_latency_ms: 0.4,
      p95_pipeline_latency_ms: 1.1,
      input_mode: 'sab',
      score: 1200,
      batch_seq: 4,
      is_final: true,
      client_ts_ms: Date.now(),
    });
    props.toast('Mock telemetry batch queued');
    void refresh();
  };

  const onFile = async (e: Event) => {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    try {
      const result = await importGameFile(file);
      props.api()?.insertGame(result.game);
      const notes = result.notes.length ? ` · ${result.notes.join(' · ')}` : '';
      props.toast(`Loaded ${result.game.title} (${(result.bytes / 1024).toFixed(1)} KB)${notes}`);
    } catch (err) {
      props.toast(`Import failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const budgetPct = () => {
    const m = memory();
    return m && m.bytes ? Math.min(100, (m.bytes / MEMORY_BUDGET_BYTES) * 100) : 0;
  };

  const hasRafInWorker = typeof (globalThis as { requestAnimationFrame?: unknown }).requestAnimationFrame === 'function';

  return (
    <aside class="dev-toolbar" data-ui aria-label="Developer toolbar">
      <div style={{ display: 'flex', 'justify-content': 'space-between', 'align-items': 'center', 'margin-bottom': '8px' }}>
        <strong>Dev toolbar</strong>
        <button class="btn" onClick={props.onClose}>
          Close
        </button>
      </div>

      <section>
        <h2>Environment</h2>
        <div class="kv">
          <span class="k">crossOriginIsolated</span>
          <span class="v" classList={{ ok: crossOriginIsolated, bad: !crossOriginIsolated }}>
            {String(crossOriginIsolated)}
          </span>
          <span class="k">SharedArrayBuffer input</span>
          <span class="v" classList={{ ok: isSharedInputAvailable(), bad: !isSharedInputAvailable() }}>
            {isSharedInputAvailable() ? (props.api()?.forcePostMessage() ? 'available (forced off)' : 'active') : 'unavailable → postMessage'}
          </span>
          <span class="k">OffscreenCanvas</span>
          <span class="v" classList={{ ok: 'OffscreenCanvas' in window }}>
            {String('OffscreenCanvas' in window)}
          </span>
          <span class="k">rAF on this thread</span>
          <span class="v">{String(hasRafInWorker)}</span>
          <span class="k">service worker</span>
          <span class="v" classList={{ ok: serviceWorkerActive() }}>
            {serviceWorkerActive() ? 'controlling' : import.meta.env.DEV ? 'off in dev (add ?sw=1)' : 'not yet'}
          </span>
          <span class="k">devicePixelRatio</span>
          <span class="v">{window.devicePixelRatio}</span>
          <span class="k">viewport</span>
          <span class="v">
            {window.innerWidth}×{window.innerHeight}
          </span>
        </div>
      </section>

      <section>
        <h2>Slots (N-1 / N / N+1)</h2>
        <div class="hosts">
          <For each={hosts()}>
            {(host) => (
              <div class="row" classList={{ active: host.meta.id === props.api()?.activeHost()?.meta.id }}>
                <span>#{host.serial}</span>
                <span>
                  {host.meta.id}
                  <Show when={host.ready}>
                    <span style={{ color: 'var(--fg-faint)' }}>
                      {' '}
                      · {host.ready!.gl} · compile {host.ready!.compileMs}ms · init {host.ready!.initMs}ms · {(host.ready!.wasmBytes / 1024).toFixed(1)}KB
                    </span>
                  </Show>
                  <Show when={host.ready?.imports.missing.length}>
                    <span class="bad"> · missing: {host.ready!.imports.missing.join(', ')}</span>
                  </Show>
                  <Show when={host.lastError}>
                    <span class="bad">
                      {' '}
                      · {host.lastError!.phase}: {host.lastError!.message}
                    </span>
                  </Show>
                </span>
                <span class={`state-${host.state}`}>{host.state}</span>
                <span>{host.workerAlive ? 'worker●' : 'worker○'}</span>
              </div>
            )}
          </For>
          <Show when={!hosts().length}>
            <div class="row">no live slots</div>
          </Show>
        </div>
        <div class="kv" style={{ 'margin-top': '6px' }}>
          <span class="k">live workers</span>
          <span class="v" classList={{ ok: hosts().filter((h) => h.workerAlive).length <= 3, bad: hosts().filter((h) => h.workerAlive).length > 3 }}>
            {hosts().filter((h) => h.workerAlive).length} / 3 max
          </span>
        </div>
      </section>

      <section>
        <h2>Memory (budget {formatMB(MEMORY_BUDGET_BYTES)})</h2>
        <div class="kv">
          <span class="k">{memory()?.source ?? 'measuring…'}</span>
          <span class="v" classList={{ ok: budgetPct() < 100, bad: budgetPct() >= 100 }}>
            {memory() ? (memory()!.bytes ? formatMB(memory()!.bytes) : 'n/a') : '…'}
          </span>
        </div>
        <div style={{ height: '6px', background: 'var(--line)', 'border-radius': '3px', margin: '6px 0' }}>
          <div
            style={{
              width: `${budgetPct()}%`,
              height: '100%',
              'border-radius': '3px',
              background: budgetPct() < 80 ? 'var(--ok)' : budgetPct() < 100 ? 'var(--accent-2)' : 'var(--danger)',
            }}
          />
        </div>
        <Show when={memory()?.breakdown?.length}>
          <div class="kv">
            <For each={memory()!.breakdown!.filter((b) => b.bytes > 512 * 1024).slice(0, 8)}>
              {(b) => (
                <>
                  <span class="k">{b.scope}</span>
                  <span class="v">{formatMB(b.bytes)}</span>
                </>
              )}
            </For>
          </div>
        </Show>
      </section>

      <section>
        <h2>Telemetry transport</h2>
        <div class="kv">
          <span class="k">mode</span>
          <span class="v">{status().mode}</span>
          <span class="k">websocket</span>
          <span class="v" classList={{ ok: status().wsState === 'open', bad: status().wsState === 'failed' }}>
            {status().wsState}
          </span>
          <span class="k">endpoint</span>
          <span class="v">{status().endpoint}</span>
          <span class="k">batches shipped</span>
          <span class="v">{status().sentBatches}</span>
          <span class="k">failed</span>
          <span class="v" classList={{ bad: status().failed > 0 }}>
            {status().failed}
          </span>
          <span class="k">SW game cache</span>
          <span class="v">{cache() ? `${cache()!.games.length} file(s)` : '—'}</span>
        </div>
        <Show when={lastBatch()}>
          <div class="log" style={{ 'margin-top': '6px' }}>
            {JSON.stringify(lastBatch(), null, 1)}
          </div>
        </Show>
      </section>

      <section>
        <h2>Actions</h2>
        <div class="actions">
          <input ref={fileInput} type="file" accept=".zip,.wasm,.html,.htm,application/wasm,application/zip,text/html" style={{ display: 'none' }} onChange={onFile} />
          <button class="btn primary" onClick={() => fileInput.click()}>
            Upload card (.html / .wasm / .zip)
          </button>
          <button class="btn" onClick={injectMock}>
            Inject mock telemetry
          </button>
          <button class="btn" onClick={() => void props.api()?.prefetchNext().then(() => refresh())}>
            Prefetch N+2 now
          </button>
          <button class="btn" onClick={() => void refresh()}>
            Measure memory
          </button>
          <button class="btn" onClick={() => void clearGameCache().then(() => refresh())}>
            Clear game cache
          </button>
          <button
            class="btn"
            onClick={() => {
              const api = props.api();
              if (!api) return;
              api.setForcePostMessage(!api.forcePostMessage());
              props.toast(`Input path for NEW slots: ${api.forcePostMessage() ? 'postMessage' : 'SharedArrayBuffer'}`);
            }}
          >
            Toggle input path
          </button>
          <button class="btn danger" onClick={() => props.api()?.activeHost()?.debugCrash()}>
            Crash active game
          </button>
        </div>
      </section>

      <section>
        <h2>Log</h2>
        <div class="log">{(tick(), props.api()?.logs().slice(-40).join('\n')) || 'no events yet'}</div>
      </section>
    </aside>
  );
}
